import React, { useCallback, useEffect, useState } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import {
  getSimulationEngine,
  getTestRunner,
  registerGameplayScenarios,
  TestResult
} from '../../../testing';
import { ScenarioRunner } from './Tests/ScenarioRunner';
import { PerformanceBenchmark } from './Tests/PerformanceBenchmark';
import { sectionHeaderStyle } from './Tests/styles';

interface TestToolsTabProps {
    onAction: (action: string, data?: any) => void;
}

interface TestScenario {
    name: string;
    description: string;
}

export const TestToolsTab: React.FC<TestToolsTabProps> = ({ onAction }) => {
  const [availableScenarios, setAvailableScenarios] = useState<TestScenario[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [simulationStats, setSimulationStats] = useState<Record<string, number> | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [showVisualFeedback, setShowVisualFeedback] = useState(true);

  // Initialize test runner on mount
  useEffect(() => {
    const runner = getTestRunner();
    registerGameplayScenarios();

    const scenarios = runner.getAvailableScenarios().map(name => ({
      name,
      description: getScenarioDescription(name)
    }));

    setAvailableScenarios(scenarios);
  }, []);

  // Get description for a scenario
  const getScenarioDescription = (name: string): string => {
    const descriptions: Record<string, string> = {
      'XP Gain and Level Up Flow': 'Tests XP gain, level up, and stat point award',
      'Stat Point Spending': 'Tests spending stat points on primary stats',
      'NPC Combat and XP Reward': 'Tests damaging NPC and receiving XP on kill',
      'Chat Message Send and Receive': 'Tests sending chat messages',
      'Teleport Functionality': 'Tests teleporting to coordinates',
      'Character Save and Load': 'Tests character data persistence',
      'Inventory Item Usage': 'Tests using items from inventory',
      'Database Persistence Verification': 'Verifies stats are saved to and loaded from database',
      'Stat Synchronization': 'Verifies all primary and derived stats sync correctly',
      'Entity Limit Stress Test': 'Tests performance with maximum entities',
      'Physics Simulation Benchmark': 'Benchmarks physics calculations performance',
      'Collision Detection Benchmark': 'Tests collision detection performance',
      'Memory Usage Stability': 'Checks for memory leaks during extended simulation',
      'Long-running Stability': 'Tests stability over extended simulation time'
    };
    return descriptions[name] || 'No description available';
  };

  // Run all tests
  const runAllTests = useCallback(async () => {
    setIsRunning(true);
    setTestResults([]);

    gameLogger.info('TEST', '🧪 Starting all test scenarios...');

    if (showVisualFeedback) {
      onAction('show_floating_text', { text: '🧪 Running Tests...', x: 400, y: 300, color: '#3498db' });
    }

    const runner = getTestRunner();
    const results = await runner.runAll({ stopOnFailure: false, verbose: false });

    setTestResults(results);
    setIsRunning(false);

    const passed = results.filter(r => r.passed).length;
    const failed = results.filter(r => !r.passed).length;

    gameLogger.info('TEST', `✅ ${passed} passed, ❌ ${failed} failed`);

    if (showVisualFeedback) {
      const status = failed === 0 ? '✅' : '⚠️';
      const color = failed === 0 ? '#2ecc71' : '#f1c40f';
      onAction('show_floating_text', {
        text: `${status} Tests: ${passed}/${results.length}`,
        x: 400,
        y: 300,
        color
      });
    }
  }, [onAction, showVisualFeedback]);

  // Run single scenario
  const runSingleScenario = useCallback(async (scenarioName: string) => {
    setIsRunning(true);

    gameLogger.info('TEST', `▶️ Running: ${scenarioName}`);

    if (showVisualFeedback) {
      onAction('show_floating_text', {
        text: `▶️ ${scenarioName}`,
        x: 400,
        y: 300,
        color: '#3498db'
      });
    }

    const runner = getTestRunner();
    const result = await runner.runScenario(scenarioName, false);

    setTestResults(prev => [...prev.filter(r => r.name !== result.name), result]);
    setIsRunning(false);

    if (showVisualFeedback) {
      const status = result.passed ? '✅' : '❌';
      const color = result.passed ? '#2ecc71' : '#e74c3c';
      onAction('show_floating_text', {
        text: `${status} ${scenarioName}`,
        x: 400,
        y: 300,
        color
      });
    }
  }, [onAction, showVisualFeedback]);

  // Run simulation benchmark
  const runBenchmark = useCallback(async () => {
    const sim = getSimulationEngine();
    sim.reset();

    // Add test entities
    sim.addEntity({ type: 'player', x: 100, y: 100, vx: 50, vy: 0 });
    sim.addEntity({ type: 'npc', x: 300, y: 100, vx: -30, vy: 0 });
    sim.addEntity({ type: 'npc', x: 500, y: 200, vx: 20, vy: -10 });
    sim.addEntity({ type: 'projectile', x: 50, y: 50, vx: 100, vy: 50 });

    const startTime = performance.now();
    await sim.runFrames(3600); // 1 minute at 60 FPS
    const duration = performance.now() - startTime;

    const stats = sim.getStats();
    stats['benchmarkDuration'] = duration;

    setSimulationStats(stats);

    gameLogger.info('TEST', 'Benchmark complete', stats);

    if (showVisualFeedback) {
      onAction('show_floating_text', {
        text: `⚡ Benchmark: ${duration.toFixed(0)}ms`,
        x: 400,
        y: 300,
        color: '#9b59b6'
      });
    }
  }, [onAction, showVisualFeedback]);

  // Export results
  const exportResults = () => {
    const report = {
      timestamp: new Date().toISOString(),
      summary: {
        total: testResults.length,
        passed: testResults.filter(r => r.passed).length,
        failed: testResults.filter(r => !r.passed).length,
        duration: testResults.reduce((sum, r) => sum + r.duration, 0)
      },
      results: testResults,
      simulationStats
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    gameLogger.info('TEST', 'Test report exported');
  };

  return (
    <>
      <ScenarioRunner
        isRunning={isRunning}
        selectedScenario={selectedScenario}
        availableScenarios={availableScenarios}
        testResults={testResults}
        showVisualFeedback={showVisualFeedback}
        onSetSelectedScenario={setSelectedScenario}
        onSetShowVisualFeedback={setShowVisualFeedback}
        onRunAll={runAllTests}
        onRunSingle={runSingleScenario}
        onClear={() => { setTestResults([]); setSimulationStats(null); }}
        onExport={exportResults}
      />

      <PerformanceBenchmark
        isRunning={isRunning}
        simulationStats={simulationStats}
        onRunBenchmark={runBenchmark}
      />

      <div style={sectionHeaderStyle}>Available Scenarios</div>
      <div style={{ fontSize: '11px', color: '#888', maxHeight: '150px', overflowY: 'auto' }}>
        {availableScenarios.map((scenario, idx) => (
          <div key={`${scenario.name}-${idx}`} style={{ marginBottom: '4px', padding: '4px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ color: '#ccc' }}>{scenario.name}</div>
            <div style={{ fontSize: '10px', color: '#666' }}>
              {scenario.description}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default TestToolsTab;
